import { CopyableGuid } from "@components/CopyableText";
import React from "react";

import { DetailsList, IColumn } from "@bps/fluent-ui";
import { ProviderAppointmentType } from "@libs/api/gateways/best-health/practice/best-health-ops-gateway.dtos";
import { DotFontIcon } from "./DotFontIcon";
import { YesOrNoIcon } from "@components/YesOrNoIcon";

interface ProvidersAppointmentTypeTableProps {
  appointmentTypes: ProviderAppointmentType[];
}

export const ProvidersAppointmentTypeTable: React.FC<ProvidersAppointmentTypeTableProps> = ({
  appointmentTypes
}) => {
  const columns: IColumn[] = [
    {
      key: "appointmentTypeId",
      name: "Appointment Type Id",
      fieldName: "appointmentTypeId",
      isResizable: true,
      minWidth: 100,
      maxWidth: 150,
      onRender: (item: ProviderAppointmentType) => (
        <CopyableGuid short value={item.appointmentTypeId} />
      )
    },
    {
      key: "locationId",
      name: "Location Id",
      fieldName: "locationId",
      isResizable: true,
      minWidth: 80,
      maxWidth: 120,
      onRender: (item: ProviderAppointmentType) => (
        <CopyableGuid short value={item.locationId} />
      )
    },
    {
      key: "name",
      name: "Name",
      fieldName: "name",
      isResizable: true,
      minWidth: 100,
      maxWidth: 250
    },
    {
      key: "newPatients",
      name: "Accepts new patients",
      fieldName: "providerNewPatient",
      isResizable: true,
      minWidth: 100,
      maxWidth: 150,
      onRender: (item: ProviderAppointmentType) => {
        if (item.providerNewPatient != null) {
          return (
            <>
              <YesOrNoIcon when={item.providerNewPatient === true} />;
              <DotFontIcon iconStyles={{ root: { fontSize: 8 } }} />
            </>
          );
        } else {
          return <YesOrNoIcon when={item.locationNewPatients === true} />;
        }
      }
    },
    {
      key: "locationExistingPatients",
      name: "Accepts existing patients",
      fieldName: "locationExistingPatients",
      isResizable: true,
      minWidth: 100,
      maxWidth: 170,
      onRender: (item: ProviderAppointmentType) => {
        if (item.providerExistingPatient != null) {
          return (
            <>
              <YesOrNoIcon when={item.providerExistingPatient === true} />
              <DotFontIcon iconStyles={{ root: { fontSize: 8 } }} />
            </>
          );
        } else {
          return <YesOrNoIcon when={item.locationExistingPatients === true} />;
        }
      }
    },
    {
      key: "newPatientDuration",
      name: "New Patient Duration (mins)",
      fieldName: "newPatientDuration",
      isResizable: true,
      minWidth: 150,
      maxWidth: 200
    },
    {
      key: "existingPatientDuration",
      name: "Existing Patient Duration (mins)",
      fieldName: "existingPatientDuration",
      isResizable: true,
      minWidth: 200,
      maxWidth: 250
    },
    {
      key: "existingPatientSupportingProviderConfigId",
      name: "Existing Patient Supporting Provider",
      fieldName: "existingPatientSupportingProviderConfigId",
      isResizable: true,
      minWidth: 150,
      maxWidth: 250,
      onRender: (item: ProviderAppointmentType) => {
        return (
          <YesOrNoIcon
            when={item.existingPatientSupportingProviderConfigId !== undefined}
          />
        );
      }
    },
    {
      key: "customAvailability",
      name: "Has Custom Availability",
      fieldName: "customAvailabilityCount",
      isResizable: true,
      minWidth: 150,
      maxWidth: 250,
      onRender: (item: ProviderAppointmentType) => {
        return <YesOrNoIcon when={item.customAvailabilityCount > 0} />;
      }
    },
    {
      key: "newPatientSupportingProviderConfigId",
      name: "New Patient Supporting Provider",
      fieldName: "newPatientSupportingProviderConfigId",
      isResizable: true,
      minWidth: 150,
      maxWidth: 250,
      onRender: (item: ProviderAppointmentType) => {
        return (
          <YesOrNoIcon
            when={item.newPatientSupportingProviderConfigId !== undefined}
          />
        );
      }
    },
    {
      key: "callClinic",
      name: "Call Clinic",
      fieldName: "callClinic",
      isResizable: true,
      minWidth: 100,
      maxWidth: 150,
      onRender: (item: ProviderAppointmentType) => {
        return <YesOrNoIcon when={item.callClinic === true} />;
      }
    }
  ];
  return <DetailsList columns={columns} items={appointmentTypes} />;
};
